import { useEffect, useRef, useState } from "react";

import { getGeoUnit } from "../../geo/api/geo";
import { findRootRegionFromPath } from "../../geo/lib/locationGeo";
import GeoSettlementAutocomplete from "../../geo/widgets/GeoSettlementAutocomplete";
import {
  applyCanonicalSettlementRejection,
  applyCanonicalSettlementSelection,
  KATOTTG_CODE_RE,
  SETTLEMENT_VERIFICATION_STATUS,
} from "../lib/searchFilterContract";

const SELECTABLE_CATEGORIES = new Set(["M", "C", "X", "K", "T"]);

function settlementBelongsToRegion(unit, regionCode) {
  if (!unit || !regionCode) return false;
  if (unit.code === regionCode && unit.category === "K") {
    return true;
  }
  const root = findRootRegionFromPath(unit.path);
  return root?.code === regionCode;
}

// Canonical KATOTTG settlement picker for public Search/Map filters (RIC-5.6E2).

function CanonicalSettlementFilterSelect({
  filters,
  onChange,
  id = "sf-settlement-code",
}) {
  const regionCode = (filters.region_code || "").trim();
  const settlementCode = (filters.settlement_code || "").trim();
  const verified = Boolean(filters.settlement_code_verified);
  const status = filters.settlement_verification_status
    || (verified
      ? SETTLEMENT_VERIFICATION_STATUS.verified
      : (settlementCode
        ? SETTLEMENT_VERIFICATION_STATUS.pending
        : SETTLEMENT_VERIFICATION_STATUS.idle));

  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const filtersRef = useRef(filters);
  const onChangeRef = useRef(onChange);
  filtersRef.current = filters;
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!regionCode || !settlementCode) {
      setSelectedSettlement(null);
      return undefined;
    }

    if (
      verified
      && status === SETTLEMENT_VERIFICATION_STATUS.verified
      && selectedSettlement?.code === settlementCode
    ) {
      return undefined;
    }

    if (
      status === SETTLEMENT_VERIFICATION_STATUS.rejected
      || status === SETTLEMENT_VERIFICATION_STATUS.error
    ) {
      return undefined;
    }

    if (!KATOTTG_CODE_RE.test(settlementCode)) {
      onChangeRef.current(
        applyCanonicalSettlementRejection(filtersRef.current, {
          status: SETTLEMENT_VERIFICATION_STATUS.rejected,
          error: "Некоректний населений пункт.",
        }),
      );
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    getGeoUnit(settlementCode, { signal: controller.signal })
      .then((unit) => {
        if (cancelled) return;
        const valid =
          unit.is_active
          && SELECTABLE_CATEGORIES.has(unit.category)
          && settlementBelongsToRegion(unit, regionCode);
        if (!valid) {
          onChangeRef.current(
            applyCanonicalSettlementRejection(filtersRef.current, {
              status: SETTLEMENT_VERIFICATION_STATUS.rejected,
              error: "Некоректний або неактивний населений пункт.",
            }),
          );
          setSelectedSettlement(null);
          return;
        }
        setSelectedSettlement(unit);
        onChangeRef.current(
          applyCanonicalSettlementSelection(filtersRef.current, unit.code, {
            verified: true,
          }),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        if (err && (err.name === "CanceledError" || err.code === "ERR_CANCELED")) {
          return;
        }
        const statusCode = err && err.response && err.response.status;
        const notFound = statusCode === 404 || statusCode === 400;
        onChangeRef.current(
          applyCanonicalSettlementRejection(filtersRef.current, {
            status: notFound
              ? SETTLEMENT_VERIFICATION_STATUS.rejected
              : SETTLEMENT_VERIFICATION_STATUS.error,
            error: notFound
              ? "Некоректний або неактивний населений пункт."
              : "Не вдалося перевірити населений пункт. Спробуйте ще раз.",
          }),
        );
        setSelectedSettlement(null);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // selectedSettlement is read only to skip a repeat lookup after label restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionCode, settlementCode, verified, status]);

  const handleSelect = (settlement) => {
    if (!settlement?.code) {
      setSelectedSettlement(null);
      onChange(applyCanonicalSettlementSelection(filters, ""));
      return;
    }
    setSelectedSettlement(settlement);
    onChange(applyCanonicalSettlementSelection(filters, settlement.code));
  };

  return (
    <GeoSettlementAutocomplete
      id={id}
      regionCode={regionCode || undefined}
      enabled={Boolean(regionCode)}
      selectedSettlement={selectedSettlement}
      onSelect={handleSelect}
      disabled={!regionCode}
      disabledHelp={
        !regionCode
          ? "Спочатку оберіть область або регіон."
          : undefined
      }
    />
  );
}

export default CanonicalSettlementFilterSelect;
